/**
 * Admin Security Proof — live tenant-isolation evidence (admin-only)
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { CheckCircle2, XCircle, Shield, RefreshCcw, Lock, FileCheck2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSecurityProof = () => {
  const [loading, setLoading] = useState(true);
  const [proof, setProof] = useState(null);
  const [responseHeaders, setResponseHeaders] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/admin/security-proof/run`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const headerSnapshot = {};
      ['x-cartie-org-id', 'x-cartie-tenant-scope', 'strict-transport-security',
       'cross-origin-opener-policy', 'cross-origin-resource-policy',
       'x-frame-options', 'referrer-policy', 'x-content-type-options']
        .forEach((h) => { headerSnapshot[h] = r.headers.get(h); });
      setResponseHeaders(headerSnapshot);
      setProof(await r.json());
    } catch (e) { toast.error('Could not run security proof'); }
    finally { setLoading(false); }
  };

  useEffect(() => { run(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div className="p-6 sm:p-8 max-w-5xl mx-auto space-y-6" data-testid="security-proof-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2"><Shield className="h-7 w-7 text-emerald-500" /><h1 className="text-3xl sm:text-4xl font-bold tracking-tight"><BrandTitle>Security Proof</BrandTitle></h1></div>
          <p className="text-slate-300 max-w-2xl">Live, in-process tenant-isolation checks. Hit <strong>Re-run</strong> on a sales call — results are real, not cached.</p>
        </div>
        <Button onClick={run} disabled={loading} data-testid="rerun-proof-btn"><RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />{loading ? 'Running…' : 'Re-run'}</Button>
      </div>

      {loading ? <Skeleton className="h-32" /> : proof ? (
        <Card className={`border-2 ${proof.all_passed ? 'border-emerald-500/60 bg-gradient-to-br from-emerald-500/10 to-transparent' : 'border-rose-500/60 bg-gradient-to-br from-rose-500/10 to-transparent'}`} data-testid="overall-result-card">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {proof.all_passed ? <CheckCircle2 className="h-12 w-12 text-emerald-500" /> : <XCircle className="h-12 w-12 text-rose-500" />}
              <div>
                <h2 className={`text-2xl font-bold ${proof.all_passed ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>{proof.all_passed ? 'All checks passed' : 'Failures detected'}</h2>
                <p className="text-sm text-slate-300">{proof.passed} of {proof.total} security guarantees verified · scoped to <span className="font-mono text-xs">{proof.audit_meta?.scoped_org_id}</span></p>
              </div>
            </div>
            <Badge className={`text-sm ${proof.all_passed ? 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30 dark:text-emerald-300' : 'bg-rose-500/15 text-rose-700 border-rose-500/30 dark:text-rose-300'}`}>{proof.passed}/{proof.total}</Badge>
          </CardContent>
        </Card>
      ) : null}

      <div className="space-y-3" data-testid="checks-list">
        {(proof?.checks || []).map((c) => (
          <Card key={c.id} className={c.passed ? 'border-emerald-500/30' : 'border-rose-500/40'} data-testid={`check-${c.id}`}>
            <CardContent className="p-4"><div className="flex items-start gap-3">
              {c.passed ? <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" /> : <XCircle className="h-5 w-5 text-rose-500 mt-0.5 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="font-semibold">{c.title}</p>
                <code className="text-xs text-slate-300">{c.id}</code>
                <div className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
                  {Object.entries(c.evidence || {}).map(([k, v]) => (
                    <div key={k} className="flex items-baseline gap-2 break-all">
                      <span className="text-slate-300">{k}:</span>
                      <span className={typeof v === 'boolean' ? (v ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500') : ''}>{Array.isArray(v) ? `[${v.join(', ') || 'empty'}]` : String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div></CardContent>
          </Card>
        ))}
      </div>

      {responseHeaders && (
        <Card data-testid="wire-headers-card">
          <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Lock className="h-5 w-5 text-emerald-400" />Wire-level proof (response headers your browser actually received)</CardTitle><CardDescription>InfoSec can capture this themselves with curl — no need to trust our screenshot.</CardDescription></CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted/40 rounded-lg p-4 overflow-x-auto">{Object.entries(responseHeaders).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join('\n')}</pre>
            <p className="text-xs text-slate-300 mt-3 flex items-start gap-2"><FileCheck2 className="h-4 w-4 shrink-0 mt-0.5" /><span><code>X-CARTIE-Org-ID</code> watermarks every response with the requester's org id (truncated for safe logging) so misconfigured proxies can't silently serve cross-tenant data.</span></p>
          </CardContent>
        </Card>
      )}

      <Card className="border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-transparent" data-testid="stripe-faq-card">
        <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><AlertTriangle className="h-5 w-5 text-amber-500" />What to tell prospects about Stripe keys</CardTitle><CardDescription>Customers will ask. This is the answer that closes the deal.</CardDescription></CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="p-3 rounded-lg bg-slate-800/50 border border-emerald-500/20"><p className="font-semibold text-emerald-600 dark:text-emerald-300 mb-1">✓ We never ask for the live secret key (sk_live_…)</p><p className="text-slate-300">We use Stripe <strong>Restricted Keys</strong> with read-only scope on Customer/Subscription/Invoice. Cannot refund, charge, or access cards. Revocable in 5 seconds.</p></div>
            <div className="p-3 rounded-lg bg-slate-800/50 border border-emerald-500/20"><p className="font-semibold text-emerald-600 dark:text-emerald-300 mb-1">✓ Enterprise: Stripe Connect OAuth</p><p className="text-slate-300">Customer clicks "Connect Stripe", lands on stripe.com, approves once. We never see a key.</p></div>
            <div className="p-3 rounded-lg bg-slate-800/50 border border-emerald-500/20"><p className="font-semibold text-emerald-600 dark:text-emerald-300 mb-1">✓ Paranoid: CSV upload</p><p className="text-slate-300">Drop a Stripe export CSV once a month. Same dashboard, zero credential sharing.</p></div>
            <div className="p-3 rounded-lg bg-slate-800/50 border border-emerald-500/20"><p className="font-semibold text-emerald-600 dark:text-emerald-300 mb-1">✓ Encrypted at rest, never logged</p><p className="text-slate-300">Audit log auto-redacts <code>sk_*</code> and <code>rk_*</code> patterns. Keys live in env, never in DB, never echoed.</p></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSecurityProof;
